import React, { useEffect, useState, useRef } from "react";
import { Monitor, Package, Clock, Network, Zap, Layers, Fingerprint, Share2, EthernetPort } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Label } from "recharts";
import ReactFlow, {
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    type Node,
    type Edge,
    BackgroundVariant,
    ConnectionMode,
    type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';

import SwitchNode from '../../AssetTopology/nodes/SwitchNode';
import AssetNode from '../../AssetTopology/nodes/AssetNode';
import CustomEdge from '../../AssetTopology/CustomEdge';
import baseapi from "../../../services/baseapi";

const parseUptimeToSeconds = (uptimeStr: string): number | null => {
    if (!uptimeStr || uptimeStr === "-") return null;
    let seconds = 0;
    const yMatch = uptimeStr.match(/(\d+)\s*y/i);
    const wMatch = uptimeStr.match(/(\d+)\s*w/i);
    const dMatch = uptimeStr.match(/(\d+)\s*d/i);
    const hMatch = uptimeStr.match(/(\d+)\s*h/i);
    const mMatch = uptimeStr.match(/(\d+)\s*m/i);
    const sMatch = uptimeStr.match(/(\d+)\s*s/i);
    if (!yMatch && !wMatch && !dMatch && !hMatch && !mMatch && !sMatch) return null;
    if (yMatch) seconds += parseInt(yMatch[1], 10) * 31536000;
    if (wMatch) seconds += parseInt(wMatch[1], 10) * 604800;
    if (dMatch) seconds += parseInt(dMatch[1], 10) * 86400;
    if (hMatch) seconds += parseInt(hMatch[1], 10) * 3600;
    if (mMatch) seconds += parseInt(mMatch[1], 10) * 60;
    if (sMatch) seconds += parseInt(sMatch[1], 10);
    return seconds;
};

const formatSecondsToUptime = (totalSeconds: number): string => {
    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${d}d ${h}h ${m}m ${s}s`;
};

const formatScanTime = (isoString?: string): string => {
    if (!isoString) return "-";
    const date = new Date(isoString + "Z"); // 🔥 force UTC
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
};

interface SwitchDetails {
    name: string;
    model: string;
    description?: string;
    serial_number: string;
    ios_version: string | null;
    ip_address: string;
    uptime: string;
    location: string;
    object_id?: string;
    model_hint?: string;
    updated?: string;
    total_interfaces: number;
    total_ports_up: number;
    total_vlans: number;
    total_mac_entries: number;
    total_cdp_neighbors: number;
    entity_class: string;
    manufacturer: string;
    boot_version: string;
}

interface PortStats {
    totalPorts: number;
    usedPorts: number;
    availablePorts: number;
    downPorts: number;
    usedPercentage: number;
    availablePercentage: number;
    downPercentage: number;
    transientPorts: number;
    transientPercentage: number;
    inactivePorts: number;
    inactivePercentage: number;
}

interface SwitchOverviewProps {
    asset_id: string;
    onDataLoaded?: (hasData: boolean) => void;
    client_id: string;
}

const PORT_COLORS = {
    used: "#22c55e",
    available: "#3b82f6",
    down: "#ef4444",
    transient: "#f59e0b",
    inactive: "#6b7280",
};

const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const { name, value, payload: p } = payload[0];
    return (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 shadow-xl">
            <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                <span className="text-[11px] font-bold text-gray-900 dark:text-white">{name}</span>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 ml-1">{value} ports</span>
            </div>
        </div>
    );
};

const nodeTypes = {
    switchNode: SwitchNode,
    assetNode: AssetNode,
};

const edgeTypes = {
    custom: CustomEdge,
};

const SwitchOverview: React.FC<SwitchOverviewProps> = ({ asset_id, client_id, onDataLoaded }) => {
    const [isDark, setIsDark] = useState<boolean>(() =>
        typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : true
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    const [details, setDetails] = useState<SwitchDetails | null>(null);
    const [portStats, setPortStats] = useState<PortStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [uptimeSeconds, setUptimeSeconds] = useState<number | null>(null);
    const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [detailsRes, assetRes, switchRes, portRes] = await Promise.all([
                baseapi.get("/switch_details", {
                    params: { query: JSON.stringify({ asset_id: parseInt(asset_id), client_id: client_id }) },
                }),
                baseapi.get('/assets', {
                    params: {
                        query: JSON.stringify(client_id ? { client_id } : {}),
                        skip: 0,
                        limit: 1000,
                        connection_check: true,
                    },
                }),
                baseapi.get(`/interfaces/switch/${asset_id}`, {
                    params: { client_id: client_id || null }
                }),
                baseapi.get("/dashboard/interface_port_overview", {
                    params: { switch_asset_id: parseInt(asset_id), client_id: client_id },
                }),
            ]);

            // Handle Details response
            let fetchedDetails: SwitchDetails | null = null;
            if (detailsRes.data.status) {
                const fetchedData = detailsRes.data.data;
                fetchedDetails = Array.isArray(fetchedData) ? fetchedData[0] : fetchedData;
                const hasData = !!(fetchedDetails && Object.keys(fetchedDetails).length > 0);

                if (hasData) {
                    setDetails(fetchedDetails);
                } else {
                    setDetails(null);
                    if (onDataLoaded) onDataLoaded(true);
                }
            } else {
                setDetails(null);
                if (onDataLoaded) onDataLoaded(true);
            }

            // Handle Ports response
            if (portRes.data.status && portRes.data.data?.stats) {
                setPortStats(portRes.data.data.stats);
            } else {
                setPortStats(null);
            }

            // Handle Flow Data
            let allAssets: any[] = [];
            if (assetRes.data.status) {
                allAssets = assetRes.data.data || [];
            }

            if (switchRes.data.status && switchRes.data.data) {
                const interfaceData = switchRes.data.data.interfaces || [];
                const connections: any[] = [];
                const seenAssetIds = new Set<number>();

                interfaceData.forEach((inf: any) => {
                    let assetsInPort: any[] = [];
                    if (inf.connected_assets) {
                        try {
                            const parsed = typeof inf.connected_assets === 'string'
                                ? JSON.parse(inf.connected_assets)
                                : inf.connected_assets;
                            if (Array.isArray(parsed)) assetsInPort = parsed;
                        } catch { /* ignore */ }
                    }

                    if (assetsInPort.length === 0 && (inf.connected_asset || inf.connected_asset_id)) {
                        const asset = inf.connected_asset || allAssets.find((a: any) => a.id === inf.connected_asset_id);
                        if (asset) {
                            assetsInPort = [{
                                asset_id: asset.id,
                                asset_name: asset.name || asset.asset_name,
                                asset_type: asset.type || asset.asset_type,
                                asset_ip: asset.ip_address || asset.asset_ip,
                                asset_status: asset.status,
                                is_uplink: (asset._is_uplink ?? false) || (inf.is_promoted && (asset.type === 'switch' || asset.asset_type === 'switch')),
                                connection_type: asset._connection_type ?? 'ethernet',
                                has_problem: asset.has_problem
                            }];
                        }
                    }

                    assetsInPort.forEach((a: any) => {
                        const connAssetId = a.asset_id || a.id;
                        if (!connAssetId) return;
                        connections.push({
                            portId: inf.id,
                            assetId: connAssetId,
                            assetName: a.asset_name || a.name || 'Linked Asset',
                            assetType: a.asset_type || a.type || 'other',
                            assetIp: a.asset_ip || a.ip_address || 'Unknown',
                            assetStatus: a.asset_status || a.status,
                            isUplink: (a.is_uplink ?? false) || (inf.is_promoted && (a.asset_type === 'switch' || a.type === 'switch')),
                            connectionType: a.connection_type || 'ethernet',
                            hasProblem: a.has_problem ?? false
                        });
                    });
                });

                const uniqueAssetConnections = connections.filter((c: any) => {
                    if (seenAssetIds.has(c.assetId)) return false;
                    seenAssetIds.add(c.assetId);
                    return true;
                });

                const switchNodeId = `detail-switch-${asset_id}`;
                const switchType = fetchedDetails?.entity_class || 'switch';
                const isAP = switchType === 'access_point';
                const centerPos = { x: 0, y: 0 };

                const switchName = fetchedDetails?.name || 'Switch';
                const switchIp = fetchedDetails?.ip_address || '';

                const switchNode: Node = {
                    id: switchNodeId,
                    type: isAP ? 'assetNode' : 'switchNode',
                    position: centerPos,
                    data: {
                        switchName: switchName,
                        assetName: switchName,
                        assetId: parseInt(asset_id),
                        assetType: switchType,
                        assetIp: switchIp,
                        status: 'active',
                        hasProblem: false,
                        portMappings: interfaceData,
                        onDelete: () => { },
                        onPortClick: () => { },
                        readOnly: true,
                    },
                    draggable: true,
                };

                const detailNodes: Node[] = [switchNode];
                const detailEdges: Edge[] = [];

                const NODE_WIDTH = 220;
                const MIN_RADIUS = 320;
                const circumferenceRadius = (uniqueAssetConnections.length * NODE_WIDTH) / (2 * Math.PI);
                const radius = Math.max(MIN_RADIUS, circumferenceRadius);
                const angleStep = (2 * Math.PI) / (uniqueAssetConnections.length || 1);
                const startAngle = -Math.PI / 2;

                uniqueAssetConnections.forEach((conn: any, index: number) => {
                    const angle = startAngle + index * angleStep;
                    const assetPos = {
                        x: centerPos.x + radius * Math.cos(angle),
                        y: centerPos.y + radius * Math.sin(angle),
                    };

                    const assetNodeId = `detail-asset-${conn.assetId}`;
                    detailNodes.push({
                        id: assetNodeId,
                        type: 'assetNode',
                        position: assetPos,
                        data: {
                            assetId: conn.assetId,
                            assetName: conn.assetName,
                            assetType: conn.assetType,
                            assetIp: conn.assetIp,
                            status: conn.assetStatus,
                            hasProblem: conn.hasProblem,
                            readOnly: true,
                        },
                        draggable: true,
                    });

                    let apTargetHandle = 'top';
                    let apSourceHandle = 'bottom';

                    if (isAP) {
                        if (angle >= -Math.PI / 4 && angle < Math.PI / 4) {
                            apSourceHandle = 'right';
                            apTargetHandle = 'right-target';
                        } else if (angle >= Math.PI / 4 && angle < 3 * Math.PI / 4) {
                            apSourceHandle = 'bottom';
                            apTargetHandle = 'bottom-target';
                        } else if (angle >= 3 * Math.PI / 4 && angle < 5 * Math.PI / 4) {
                            apSourceHandle = 'left-source';
                            apTargetHandle = 'left';
                        } else {
                            apSourceHandle = 'top-source';
                            apTargetHandle = 'top';
                        }
                    }

                    if (conn.isUplink) {
                        const upStyle = { stroke: '#10b981', strokeWidth: 2 };
                        detailEdges.push({
                            id: `detail-edge-${conn.assetId}-${asset_id}-${conn.portId}`,
                            source: assetNodeId,
                            sourceHandle: 'bottom',
                            target: switchNodeId,
                            targetHandle: isAP ? apTargetHandle : `port-in-${conn.portId}`,
                            type: 'custom',
                            animated: true,
                            style: upStyle,
                            data: {
                                connection_type: conn.connectionType,
                                is_uplink: true,
                                originalStyle: upStyle,
                                hideLabel: isAP,
                            },
                        });
                    } else {
                        const downStyle = { stroke: '#3b82f6', strokeWidth: 2 };
                        detailEdges.push({
                            id: `detail-edge-${asset_id}-${conn.assetId}-${conn.portId}`,
                            source: switchNodeId,
                            sourceHandle: isAP ? apSourceHandle : `port-${conn.portId}`,
                            target: assetNodeId,
                            targetHandle: 'top',
                            type: 'custom',
                            animated: true,
                            style: downStyle,
                            data: {
                                connection_type: conn.connectionType,
                                is_uplink: false,
                                originalStyle: downStyle,
                                hideLabel: isAP,
                            },
                        });
                    }
                });

                setNodes(detailNodes);
                setEdges(detailEdges);

                setTimeout(() => {
                    reactFlowInstanceRef.current?.fitView({ padding: 0.8, duration: 500 });
                }, 150);
            }
        } catch (error: any) {
            console.error("Error fetching switch flow details", error);
            setDetails(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (asset_id) fetchData();
    }, [asset_id]);

    useEffect(() => {
        if (!details?.uptime) return;
        const initialSeconds = parseUptimeToSeconds(details.uptime);
        if (initialSeconds === null) return;
        setUptimeSeconds(initialSeconds);
        const interval = setInterval(() => {
            setUptimeSeconds((prev) => (prev !== null ? prev + 1 : null));
        }, 1000);
        return () => clearInterval(interval);
    }, [details?.uptime]);

    const topStats = [
        { label: "Interfaces", value: details?.total_interfaces || 0, icon: Network, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10" },
        { label: "Ports Up", value: details?.total_ports_up || 0, icon: Zap, color: "text-emerald-600 dark:text-emerald-500", bg: "bg-emerald-500/10" },
        { label: "VLANs", value: details?.total_vlans || 0, icon: Layers, color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-500/10" },
        { label: "MAC Entries", value: details?.total_mac_entries || 0, icon: Fingerprint, color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-500/10" },
        { label: "Neighbors", value: details?.total_cdp_neighbors || 0, icon: Share2, color: "text-sky-600 dark:text-sky-400", bg: "bg-sky-500/10" },
    ];

    const pieData = portStats
        ? [
            { name: "Used", value: portStats.usedPorts, color: PORT_COLORS.used, pct: portStats.usedPercentage },
            { name: "Available", value: portStats.availablePorts, color: PORT_COLORS.available, pct: portStats.availablePercentage },
            { name: "Down", value: portStats.downPorts, color: PORT_COLORS.down, pct: portStats.downPercentage },
            { name: "Inactive", value: portStats.inactivePorts, color: PORT_COLORS.inactive, pct: portStats.inactivePercentage },
        ]
        : [];

    if (loading) {
        return (
            <div className="space-y-4 animate-fade-in">
                <div className="flex gap-2">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-16 flex-1 bg-slate-200/70 dark:bg-slate-800/50 rounded-xl animate-pulse border border-slate-300/60 dark:border-slate-700/50" />
                    ))}
                </div>
                <div className="flex gap-4">
                    <div className="h-[550px] flex-[7] bg-slate-200/70 dark:bg-slate-800/50 rounded-xl animate-pulse border border-slate-300/60 dark:border-slate-700/50" />
                    <div className="h-[550px] flex-[3] bg-slate-200/70 dark:bg-slate-800/50 rounded-xl animate-pulse border border-slate-300/60 dark:border-slate-700/50" />
                </div>
                <div className="h-44 bg-slate-200/70 dark:bg-slate-800/50 rounded-xl animate-pulse border border-slate-300/60 dark:border-slate-700/50" />
                <div className="h-44 bg-slate-200/70 dark:bg-slate-800/50 rounded-xl animate-pulse border border-slate-300/60 dark:border-slate-700/50" />
            </div>
        );
    }

    if (!details) {
        return (
            <div className="w-full flex items-center justify-center p-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm dark:shadow-lg">
                <span className="text-slate-500 dark:text-slate-400 font-semibold tracking-wide">No switch details found.</span>
            </div>
        );
    }

    return (
        <div className="animate-fade-in space-y-4">
            {/* ─── Top Stats Ribbon ─── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {topStats.map((stat, idx) => (
                    <div
                        key={idx}
                        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 shadow-sm dark:shadow-md flex items-center justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                    >
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest mb-0.5">{stat.label}</span>
                            <span className="text-xl font-bold text-gray-900 dark:text-white leading-none">{stat.value}</span>
                        </div>
                        <div className={`p-2 rounded-lg ${stat.bg} border border-slate-200/50 dark:border-white/5`}>
                            <stat.icon className={`w-4 h-4 ${stat.color}`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* ─── Row 2: Topology Flow (70%) & Port Overview (30%) ─── */}
            <div className="flex flex-col lg:flex-row gap-4">
                {/* Topology Flow (70%) */}
                <div className="w-full lg:w-[70%] bg-white dark:bg-[#0b0e14]/50 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm dark:shadow-lg flex flex-col h-[550px]">
                    <div className="flex items-center justify-between mb-3 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-50 dark:bg-slate-800 p-1.5 rounded-lg">
                                <Network className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h2 className="text-md font-bold text-gray-900 dark:text-white">Topology Flow</h2>
                        </div>
                    </div>
                    <hr className="border-slate-200 dark:border-slate-800 mb-4 flex-shrink-0" />
                    
                    <div className="flex-1 w-full relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#090b0f] min-h-0">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            connectionRadius={30}
                            connectionMode={ConnectionMode.Loose}
                            onInit={(instance) => { reactFlowInstanceRef.current = instance; }}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            minZoom={0.2}
                            maxZoom={2.0}
                            fitView
                            fitViewOptions={{ padding: 0.8 }}
                            style={{ background: isDark ? '#090b0f' : '#f8fafc' }}
                            proOptions={{ hideAttribution: true }}
                            nodesDraggable={true}
                            nodesConnectable={false}
                            elementsSelectable={false}
                            deleteKeyCode={null}
                        >
                            <Background
                                variant={BackgroundVariant.Dots}
                                gap={24}
                                size={1}
                                color={isDark ? "#1f2937" : "#cbd5e1"}
                            />
                            <Controls className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !fill-gray-600 dark:!fill-gray-400 shadow-md" />
                        </ReactFlow>
                    </div>
                </div>

                {/* Port Overview (30%) */}
                <div className="w-full lg:w-[30%] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm dark:shadow-lg flex flex-col h-[550px]">
                    <div className="flex items-center gap-3 mb-3 flex-shrink-0">
                        <div className="bg-blue-50 dark:bg-slate-800 p-1.5 rounded-lg">
                            <EthernetPort className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h2 className="text-md font-bold text-gray-900 dark:text-white">Port Overview</h2>
                    </div>
                    <hr className="border-slate-200 dark:border-slate-800 mb-4 flex-shrink-0" />

                    <div className="flex-1 flex flex-col justify-between min-h-0">
                        {portStats ? (
                            <>
                                {/* Pie */}
                                <div className="w-full flex-1 min-h-[200px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={90}
                                                paddingAngle={3}
                                                dataKey="value"
                                                strokeWidth={0}
                                                isAnimationActive={false}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={index} fill={entry.color} />
                                                ))}
                                                <Label
                                                    content={({ viewBox: { cx, cy } }: any) => {
                                                        return (
                                                            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                                                <tspan
                                                                    x={cx}
                                                                    dy="-0.2em"
                                                                    fill={isDark ? "#f8fafc" : "#0f172a"}
                                                                    fontSize="18"
                                                                    fontWeight="bold"
                                                                >
                                                                    {portStats.totalPorts}
                                                                </tspan>
                                                                <tspan
                                                                    x={cx}
                                                                    dy="1.4em"
                                                                    fill={isDark ? "#94a3b8" : "#64748b"}
                                                                    fontSize="11"
                                                                    fontWeight="bold"
                                                                    className="uppercase tracking-widest mt-1"
                                                                >
                                                                    Ports
                                                                </tspan>
                                                            </text>
                                                        );
                                                    }}
                                                />
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Legend with counts */}
                                <div className="flex flex-col gap-2 mt-2 flex-shrink-0 overflow-y-auto max-h-[220px]">
                                    {pieData.map((entry) => (
                                        <div
                                            key={entry.name}
                                            className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
                                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">{entry.name}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[11px] font-bold text-gray-900 dark:text-white">{entry.value}</span>
                                                <span className="text-[10px] text-slate-500 dark:text-slate-200 font-mono">
                                                    ({entry.pct.toFixed(1)}%)
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-600 text-[11px] font-bold uppercase tracking-widest">
                                No port data
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Row 3: Full-width Details Cards ─── */}
            <div className="space-y-4">
                {/* Device Overview (Full Width) */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm dark:shadow-lg w-full">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="bg-slate-100 dark:bg-slate-800 p-1.5 rounded-lg">
                            <Monitor className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </div>
                        <h2 className="text-md font-bold text-gray-900 dark:text-white">Device Overview</h2>
                    </div>
                    <hr className="border-slate-200 dark:border-slate-800 mb-4" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-5 gap-x-6">
                        {[
                            { label: "Device Name", value: details?.name },
                            { label: "Description", value: details?.description },
                            { label: "Location", value: details?.location },
                            { label: "IP Address", value: details?.ip_address },
                            { label: "Contact", value: undefined },
                            { label: "Object ID", value: details?.object_id, mono: true },
                            { label: "Scan Time", value: formatScanTime(details?.updated) },
                            { label: "SNMP Version", value: "v2c" },
                        ].map(({ label, value, mono }) => (
                            <div key={label} className="flex flex-col gap-0.5">
                                <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                    {label}
                                </span>

                                {label === "Description" ? (
                                    <div
                                        className="text-sm text-gray-800 dark:text-slate-200 max-h-14 overflow-y-auto pr-1 custom-scroll break-words"
                                        title={value || "-"}
                                    >
                                        {value || "-"}
                                    </div>
                                ) : (
                                    <span
                                        className={`text-sm ${mono ? "text-gray-900 dark:text-slate-200 font-mono" : "text-gray-900 dark:text-slate-200"
                                            } truncate`}
                                        title={value || "-"}
                                    >
                                        {value || "-"}
                                    </span>
                                )}
                            </div>
                        ))}

                        {/* Uptime */}
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                                Uptime
                            </span>
                            <div className="flex items-center gap-1.5 text-sm text-gray-900 dark:text-slate-200 truncate">
                                <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                <span>
                                    {uptimeSeconds !== null
                                        ? formatSecondsToUptime(uptimeSeconds)
                                        : details?.uptime || "-"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Firmware & Hardware Info (Full Width) */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm dark:shadow-lg w-full">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="bg-orange-50 dark:bg-slate-800 p-1.5 rounded-lg">
                            <Package className="w-4 h-4 text-orange-500" />
                        </div>
                        <h2 className="text-md font-bold text-gray-900 dark:text-white">Firmware & Hardware Info</h2>
                    </div>
                    <hr className="border-slate-200 dark:border-slate-800 mb-4" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-5 gap-x-6">
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Manufacturer</span>
                            <span className="text-sm text-gray-900 dark:text-slate-200 truncate">
                                {details?.manufacturer || "-"}
                            </span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Model</span>
                            <span className="text-sm text-gray-900 dark:text-slate-200 truncate" title={details?.model || "-"}>{details?.model || "-"}</span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Serial Number</span>
                            <span className="text-sm text-slate-600 dark:text-slate-400 font-mono uppercase tracking-tight truncate" title={details?.serial_number || "-"}>{details?.serial_number || "-"}</span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Boot Version</span>
                            <span className="text-sm text-gray-900 dark:text-slate-200">{details?.boot_version || "-"}</span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Firmware Version</span>
                            <span className="text-sm text-gray-900 dark:text-slate-200" title={details?.ios_version || "-"}>{details?.ios_version || "-"}</span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Entity Name</span>
                            <span className="text-sm text-gray-900 dark:text-slate-200 truncate" title={details?.model_hint || "-"}>{details?.model_hint || "-"}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SwitchOverview;